import type { IRaffle } from "react-ui-components/components/models/IRaffle"

export const POINT_TRANSFER_CODE_PARAM = "pointTransferCode"
export const POINT_TRANSFER_QR_PURPOSE = "facil-points-transfer"
export const ONE_TIME_USER_CODE_PATTERN = /^\d{9}-\d$/

const URL_LIKE_PATTERN = /^(?:[a-z][a-z\d+\-.]*:|\/|\?|www\.)/i

const getUrlParsingBase = () =>
  typeof window !== "undefined" ? window.location.origin : "https://winfacil.local"

export const isValidOneTimeUserCode = (code: string | null | undefined) =>
  ONE_TIME_USER_CODE_PATTERN.test((code || "").trim())

const KNOWN_POINT_TRANSFER_ERROR_KEYS = [
  "Code not found or already used.",
  "Code expired.",
  "You are not allowed to retrieve your own code.",
  "Sender does not have enough points.",
  "Sender not found.",
  "Receiver not found.",
  "Missing code in request data.",
  "Missing points in request data.",
  "Unauthorized: Missing or invalid authentication.",
] as const

export const normalizeTransferCodeInput = (input: string | null | undefined) => {
  const trimmed = (input || "").trim()
  if (!trimmed) {
    return ""
  }

  const extracted = extractPointTransferCodeFromScanPayload(trimmed)
  return extracted || trimmed
}

export const getPointTransferErrorMessage = (
  error: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  const rawMessage =
    typeof (error as { message?: unknown })?.message === "string"
      ? (error as { message: string }).message
      : typeof error === "string"
        ? error
        : ""
  const cleaned = rawMessage.replace(/^FirebaseError:\s*/i, "").trim()

  if (!cleaned || /^(?:internal|internal server error)$/i.test(cleaned)) {
    return t("errorRetrievingUserCode")
  }

  if (/invalid points amount/i.test(cleaned)) {
    return t("You cannot enter more points than you have available.")
  }

  for (const key of KNOWN_POINT_TRANSFER_ERROR_KEYS) {
    if (cleaned === key || cleaned.includes(key)) {
      return t(key)
    }
  }

  return t(cleaned)
}

export const buildPointTransferScanPayload = (code: string, origin = getUrlParsingBase()) => {
  const trimmedCode = code.trim()
  const payloadUrl = new URL("/", origin || "https://winfacil.local")

  payloadUrl.searchParams.set(POINT_TRANSFER_CODE_PARAM, trimmedCode)
  payloadUrl.searchParams.set("qrPurpose", POINT_TRANSFER_QR_PURPOSE)
  payloadUrl.searchParams.set("qrProvider", "sundry-qr")
  payloadUrl.searchParams.set("v", "1")

  return payloadUrl.toString()
}

export const extractPointTransferCodeFromScanPayload = (payload: string) => {
  const trimmedPayload = payload.trim()

  if (!trimmedPayload) {
    return ""
  }

  if (isValidOneTimeUserCode(trimmedPayload)) {
    return trimmedPayload
  }

  if (!URL_LIKE_PATTERN.test(trimmedPayload)) {
    return ""
  }

  try {
    const normalizedPayload = trimmedPayload.startsWith("www.")
      ? `https://${trimmedPayload}`
      : trimmedPayload
    const parsedUrl = new URL(normalizedPayload, getUrlParsingBase())
    const code =
      parsedUrl.searchParams.get(POINT_TRANSFER_CODE_PARAM)?.trim() ||
      parsedUrl.searchParams.get("userCode")?.trim() ||
      ""

    return isValidOneTimeUserCode(code) ? code : ""
  } catch {
    return ""
  }
}

export const normalizePointValue = (value: unknown) => {
  const pointValue = Number(value)
  return Number.isFinite(pointValue) && pointValue > 0 ? pointValue : 0
}

export const getRafflePointCost = (raffle: Pick<IRaffle, "points"> | null | undefined) =>
  normalizePointValue(raffle?.points)

export const getMaxTicketsForRaffle = (
  availablePoints: number,
  raffle: Pick<IRaffle, "points"> | null | undefined,
) => {
  const cost = getRafflePointCost(raffle)

  if (!cost) {
    return 0
  }

  return Math.floor(Math.max(0, availablePoints) / cost)
}

export const getAffordableRaffles = (raffles: IRaffle[], availablePoints: number) =>
  raffles.filter((raffle) => getMaxTicketsForRaffle(availablePoints, raffle) > 0)

export const clampTicketsForRaffle = (
  requestedTickets: number,
  availablePoints: number,
  raffle: Pick<IRaffle, "points"> | null | undefined,
) => {
  const maxTickets = getMaxTicketsForRaffle(availablePoints, raffle)
  const safeRequestedTickets = Number.isFinite(requestedTickets)
    ? Math.floor(requestedTickets)
    : 0

  return Math.min(Math.max(safeRequestedTickets, 0), maxTickets)
}
